import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ButtonBase, Grid, styled, TextField } from '@mui/material';
import CompanySectorSelector from 'components/CompanySectorSelector';
import CompanyStageSelector from 'components/CompanyStageSelector';
import CountrySelector from 'components/CountrySelector';
import CurrencySelector from 'components/CurrencySelector';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import Spacing from 'components/Spacing';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { UploadType } from 'further-types/files';
import { AssetType } from 'further-types/lp/asset';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import { investment as rules } from 'further-ui/validations';
import useFileUpload, { UploadFileResult } from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { InvestmentFormState } from '../../FormWrapper';

const TextButton = styled(ButtonBase)(({ theme }) => ({
  ...theme.typography.h6,
  color: theme.palette.secondary.main,
}));

const AssetDetailsForm: FC = () => {
  const { control, setValue, getValues } = useFormContext();
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();
  const { state, setState } = useFormWithStateContext<
    rules.CreateInvestmentFormValues,
    InvestmentFormState
  >();
  const { showPortalInformation } = state;
  const toggleShowPortalInformation = () => {
    setState({ ...state, showPortalInformation: !showPortalInformation });
  };

  const handleFileUpload = async (
    files: Array<UploadedFile>,
    onSuccess: (uploadFileResult: UploadFileResult) => void,
  ) => {
    if (!files?.[0]?.path) return;
    const uploadFileResult = await uploadFile(
      files[0],
      UploadType.CompanyLogos,
    );
    if (uploadFileResult.data) {
      onSuccess(uploadFileResult.data);
    } else {
      error(uploadFileResult.error.message);
    }
  };

  const onCurrencyChange = (value: CurrencyCode) => {
    setValue('asset.baseCurrency', value);
    const currentHoldingClasses = getValues('holdingClasses');
    currentHoldingClasses.forEach((_, index) => {
      setValue(`holdingClasses.${index}.currency`, value);
    });
  };

  return (
    <>
      <FormField size="md" label="Asset type">
        <Controller
          name={`asset.assetType`}
          control={control}
          defaultValue={AssetType.Company}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <BasicSelect
              {...rest}
              fullWidth
              options={[{ value: AssetType.Company, label: 'Company' }]}
              error={!!error}
              helperText={error?.message}
              placeholder="Select asset type"
            />
          )}
        />
      </FormField>
      <FormField size="md" label="Trading name*">
        <Controller
          name="asset.name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              size="small"
              fullWidth
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Trading name, e.g. Acme"
            />
          )}
        />
      </FormField>

      <FormField size="md" label="Full legal name*">
        <Controller
          name="asset.fullLegalName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              size="small"
              fullWidth
              {...field}
              error={!!error}
              helperText={error?.message}
              placeholder="Enter legal name, e.g. Acme Software Limited"
            />
          )}
        />
      </FormField>

      <FormField size="md" label="Base currency*">
        <Controller
          name="asset.baseCurrency"
          control={control}
          defaultValue={CurrencyCode.GBP}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <CurrencySelector
              {...rest}
              error={error ? { message: error.message } : undefined}
              placeholder="Select base currency"
              onChange={onCurrencyChange}
            />
          )}
        />
      </FormField>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered number">
          <Controller
            name="asset.registeredNumber"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
                placeholder="Registered number"
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registration country">
          <Controller
            name="asset.registrationCountry"
            control={control}
            defaultValue=""
            render={({ field: { ref, value }, fieldState: { error } }) => (
              <CountrySelector
                ref={ref}
                value={value}
                name="asset.registrationCountry"
                placeholder="Select registration country"
                error={error}
                onChange={(_, option) => {
                  setValue('asset.registrationCountry', option?.code || '');
                }}
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered address line 1">
          <Controller
            name="asset.registeredAddressLine1"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
                placeholder="Address line 1"
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered address line 2">
          <Controller
            name="asset.registeredAddressLine2"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
                placeholder="Address line 2"
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered address town/city">
          <Controller
            name="asset.registeredAddressTownCity"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
                placeholder="Town/city"
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered address postal code">
          <Controller
            name="asset.registeredAddressPostalCode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                size="small"
                fullWidth
                {...field}
                error={!!error}
                helperText={error?.message}
                placeholder="Postal code"
              />
            )}
          />
        </FormField>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormField size="md" label="Registered address country">
          <Controller
            name="asset.registeredAddressCountry"
            control={control}
            defaultValue=""
            render={({
              field: { ref, value, ...rest },
              fieldState: { error },
            }) => (
              <CountrySelector
                {...rest}
                ref={ref}
                value={value}
                name="asset.registeredAddressCountry"
                placeholder="Select country"
                error={error}
                onChange={(_, option) => {
                  setValue(
                    'asset.registeredAddressCountry',
                    option?.code || '',
                  );
                }}
              />
            )}
          />
        </FormField>
      </Grid>

      <Row>
        <TextButton
          color="secondary"
          onClick={() => {
            toggleShowPortalInformation();
          }}
        >
          {showPortalInformation ? (
            <Row centered>
              <span>Hide additional portal information to company record</span>
              <ExpandLess fontSize="small" color="secondary" />
            </Row>
          ) : (
            <Row centered>
              <span>Add additional portal information to company record</span>
              <ExpandMore fontSize="small" color="secondary" />
            </Row>
          )}
        </TextButton>
      </Row>

      {showPortalInformation && (
        <Spacing>
          <FormField size="md" label="Description">
            <Controller
              name="asset.description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  size="small"
                  fullWidth
                  {...field}
                  multiline
                  placeholder="Enter company description"
                  rows={3}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Logo (square 1:1)">
            <Controller
              name="asset.companyLogo"
              control={control}
              render={({ field: { ref, onChange, value, ...rest } }) => (
                <Dropzone
                  {...rest}
                  ref={ref}
                  maxFiles={1}
                  defaultAcceptGroup="images"
                  onDropFile={async (files) => {
                    await handleFileUpload(files, (uploadFileResult) => {
                      onChange({
                        path: uploadFileResult.filePath,
                        name: uploadFileResult.name,
                        preview: uploadFileResult.url,
                      });
                    });
                  }}
                  files={
                    value
                      ? [
                          {
                            preview: value.preview,
                            fileName: value.name,
                          },
                        ]
                      : []
                  }
                  onRemove={() => {
                    onChange(null);
                  }}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Website">
            <Controller
              name="asset.websiteUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  size="small"
                  fullWidth
                  {...field}
                  placeholder="Enter website URL"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Twitter/X URL">
            <Controller
              name="asset.twitterUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  size="small"
                  fullWidth
                  {...field}
                  placeholder="Enter Twitter/X URL"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="LinkedIn URL">
            <Controller
              name="asset.linkedinUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  size="small"
                  fullWidth
                  {...field}
                  placeholder="Enter LinkedIn URL"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Stage">
            <Controller
              name="asset.stage"
              control={control}
              render={({ field: { value, onChange } }) => (
                <CompanyStageSelector
                  value={value}
                  onChange={onChange}
                  name="asset.stage"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Sector">
            <Controller
              name="asset.sector"
              control={control}
              render={({ field: { value, onChange } }) => (
                <CompanySectorSelector
                  value={value}
                  onChange={onChange}
                  name="asset.sector"
                />
              )}
            />
          </FormField>
        </Spacing>
      )}
    </>
  );
};

export default AssetDetailsForm;
